"use client";

import {
  Button,
  Checkbox,
  FormInput,
  FormPassword,
  FormRoot,
  FormSelect,
  Label,
  Loader,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import type {
  ShopBranchOption,
  ShopStaffListResult,
} from "@/modules/merchant/presentation/api/client";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { InfiniteMultiCombobox } from "@/shared/ui/InfiniteMultiCombobox";
import {
  shopBranchesApi,
  shopInventoryApi,
  shopRolesApi,
  shopStaffApi,
} from "../api/client";
import { shopBranchesKeys } from "../api/queries";

const SHOP_ROLE_NONE = "__none__";

function staffUserIdFilter(userId: string): FilterConditionDTO {
  return { field: "userId", op: "eq", value: userId };
}

function parseShopRoleId(v: string | null | undefined): string | null {
  return v && v !== SHOP_ROLE_NONE ? v : null;
}

function normalizeBranchIds(raw: string[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of raw ?? []) {
    const t = id.trim();
    if (!z.string().uuid().safeParse(t).success) continue;
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

type StaffRow = ShopStaffListResult["data"][number];

/** ຫຼັງ update ບັນຊີ — ຈັດແຖວ shop_staff ຕາມສາຂາທີ່ຕ້ອງການ */
async function syncStaffBranchAssignmentsForEdit(
  shopId: string,
  userId: string,
  desiredBranchIds: string[],
  shopRoleId: string | null,
  isActive: boolean,
  priorRows: StaffRow[],
): Promise<void> {
  const desired = normalizeBranchIds(desiredBranchIds);
  const wantWide = desired.length === 0;

  if (wantWide) {
    const keep = priorRows.find((r) => r.branchId == null) ?? priorRows[0];
    if (!keep) return;
    for (const r of priorRows) {
      if (r.id !== keep.id) await shopStaffApi.delete(shopId, r.id);
    }
    await shopStaffApi.update(shopId, keep.id, {
      branchId: null,
      shopRoleId,
      isActive,
    });
    return;
  }

  for (const r of priorRows) {
    if (r.branchId == null || !desired.includes(r.branchId)) {
      await shopStaffApi.delete(shopId, r.id);
    }
  }

  let rows = (
    await shopStaffApi.list(shopId, {
      limit: 100,
      offset: 0,
      filters: [staffUserIdFilter(userId)],
      expandAssignments: true,
    })
  ).data;

  const have = new Set(
    rows
      .map((r) => r.branchId)
      .filter((id): id is string => typeof id === "string" && id.length > 0),
  );

  for (const bid of desired) {
    if (!have.has(bid)) {
      await shopStaffApi.create(shopId, {
        userId,
        shopRoleId,
        branchId: bid,
      });
    }
  }

  rows = (
    await shopStaffApi.list(shopId, {
      limit: 100,
      offset: 0,
      filters: [staffUserIdFilter(userId)],
      expandAssignments: true,
    })
  ).data;

  for (const row of rows) {
    await shopStaffApi.update(shopId, row.id, { shopRoleId, isActive });
  }
}

const CreateStaffSchema = z.object({
  email: z.string().email({ message: "ກະລຸນາໃສ່ອີເມວໃຫ້ຖືກຕ້ອງ" }),
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່"),
  password: z.string().min(6, "ລະຫັດຜ່ານຕ້ອງຢ່າງນ້ອຍ 6 ຕົວອັກສອນ").optional(),
  shopRoleId: z.string().optional().nullable(),
  branchIds: z.array(z.string().uuid()).default([]),
});

const UpdateStaffSchema = CreateStaffSchema.omit({ password: true })
  .extend({
    password: z.string().optional(),
    isActive: z.boolean(),
  })
  .partial()
  .required({ email: true, name: true });

type CreateFormValues = z.infer<typeof CreateStaffSchema>;
type UpdateFormValues = z.infer<typeof UpdateStaffSchema>;

type ShopStaffFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  mode: "create" | "edit";
  staff?: StaffRow | null;
  /** ສາຂາເລີ່ມຕົ້ນໃນໂໝດ create (ເຊັ່ນ ມາຈາກໜ້າສາຂາ → ມອບໝາຍພະນັກງານ) */
  initialBranchId?: string | null;
  onSuccess?: () => void;
};

export function ShopStaffFormModal({
  open,
  onOpenChange,
  shopId,
  mode,
  staff,
  initialBranchId = null,
  onSuccess,
}: ShopStaffFormModalProps) {
  const [loading, setLoading] = useState(true);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shopRoles, setShopRoles] = useState<{ id: string; name: string }[]>(
    [],
  );
  const createForm = RHF.useForm<CreateFormValues>({
    resolver: zodResolver(CreateStaffSchema),
    defaultValues: {
      email: "",
      name: "",
      password: "",
      shopRoleId: SHOP_ROLE_NONE,
      branchIds: [],
    },
  });

  const updateForm = RHF.useForm<UpdateFormValues>({
    resolver: zodResolver(UpdateStaffSchema),
    defaultValues: {
      email: "",
      name: "",
      password: "",
      shopRoleId: SHOP_ROLE_NONE,
      branchIds: [],
      isActive: true,
    },
  });

  const loadShopRoles = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const res = await shopRolesApi.list(shopId);
      setShopRoles(res.items.map((r) => ({ id: r.id, name: r.name })));
    } catch {
      setShopRoles([]);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  const loadEditForm = useCallback(
    async (staffData: StaffRow) => {
      setUserDetailsLoading(true);
      try {
        const [user, listRes] = await Promise.all([
          shopStaffApi.getUserDetails(shopId, staffData.id),
          shopStaffApi.list(shopId, {
            limit: 100,
            offset: 0,
            filters: [staffUserIdFilter(staffData.userId)],
            expandAssignments: true,
          }),
        ]);
        const shopWide = listRes.data.some((r) => r.branchId == null);
        const branchIds = shopWide
          ? []
          : normalizeBranchIds(
              listRes.data
                .map((r) => r.branchId)
                .filter((id): id is string => id != null && id.length > 0),
            );
        updateForm.reset({
          email: user.email,
          name: user.name,
          password: "",
          shopRoleId: staffData.shopRoleId ?? SHOP_ROLE_NONE,
          branchIds,
          isActive: staffData.isActive ?? true,
        });
      } catch {
        toast.error("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຜູ້ໃຊ້ໄດ້");
      } finally {
        setUserDetailsLoading(false);
      }
    },
    [shopId, updateForm],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ຟອມເມື່ອເປີດ modal / ປ່ຽນ staff
  useEffect(() => {
    if (!open || !shopId) return;
    loadShopRoles();
    if (mode === "create") {
      const init =
        initialBranchId?.trim() &&
        z.string().uuid().safeParse(initialBranchId.trim()).success
          ? [initialBranchId.trim()]
          : [];
      createForm.reset({
        email: "",
        name: "",
        password: "",
        shopRoleId: SHOP_ROLE_NONE,
        branchIds: init,
      });
    } else if (mode === "edit" && staff) {
      void loadEditForm(staff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shopId, mode, staff?.id, initialBranchId]);

  const preloadBranches = useCallback(
    async (ids: string[]): Promise<ShopBranchOption[]> => {
      const out: ShopBranchOption[] = [];
      for (const id of ids) {
        try {
          const b = await shopBranchesApi.get(shopId, id);
          out.push({ id: b.id, name: b.name });
        } catch {
          out.push({ id, name: `${id.slice(0, 8)}…` });
        }
      }
      return out;
    },
    [shopId],
  );

  const onSubmitCreate = async (values: CreateFormValues) => {
    setSubmitting(true);
    try {
      const roleId = parseShopRoleId(values.shopRoleId);
      const bids = normalizeBranchIds(values.branchIds);
      const firstBranch = bids[0] ?? null;

      const created = await shopStaffApi.createWithAccount(shopId, {
        email: values.email,
        name: values.name,
        password: values.password || undefined,
        shopRoleId: roleId,
        branchId: firstBranch,
      });

      for (const bid of bids.slice(1)) {
        await shopStaffApi.create(shopId, {
          userId: created.userId,
          shopRoleId: roleId,
          branchId: bid,
        });
      }

      toast.success("ເພີ່ມພະນັກງານສຳເລັດ");
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // toast from fetcher
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitUpdate = async (values: UpdateFormValues) => {
    if (!staff) return;
    setSubmitting(true);
    try {
      const roleId = parseShopRoleId(values.shopRoleId);

      await shopStaffApi.updateWithAccount(shopId, staff.id, {
        email: values.email,
        name: values.name,
        password: values.password || undefined,
        shopRoleId: roleId,
        isActive: values.isActive,
      });

      const prior = (
        await shopStaffApi.list(shopId, {
          limit: 100,
          offset: 0,
          filters: [staffUserIdFilter(staff.userId)],
          expandAssignments: true,
        })
      ).data;

      await syncStaffBranchAssignmentsForEdit(
        shopId,
        staff.userId,
        normalizeBranchIds(values.branchIds ?? []),
        roleId,
        values.isActive ?? true,
        prior,
      );

      toast.success("ແກ້ໄຂພະນັກງານສຳເລັດ");
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // toast from fetcher
    } finally {
      setSubmitting(false);
    }
  };

  const isEdit = mode === "edit";
  const shopRoleOptions = shopRoles.map((r) => ({
    label: r.name,
    value: r.id,
  }));

  const branchCombo = (
    control: typeof createForm.control | typeof updateForm.control,
    name: "branchIds",
  ) => (
    <div className="space-y-2">
      <Label>ສາຂາ (ເລືອກຫຼາຍໄດ້ · ວ່າງ = ທຸກສາຂາ)</Label>
      <RHF.Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InfiniteMultiCombobox<ShopBranchOption>
            queryKey={[...shopBranchesKeys.all(shopId), "staff-form-branches"]}
            queryFn={async ({ search: q, pageParam }) => {
              return shopInventoryApi.lookupBranches(shopId, {
                page: pageParam,
                search: q.trim() || undefined,
                limit: 20,
              });
            }}
            preloadQueryFn={preloadBranches}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            values={field.value ?? []}
            onValuesChange={(vals) => field.onChange(vals)}
            placeholder="ເລືອກສາຂາ ຫຼື ວ່າງໄວ້ສຳລັບທຸກສາຂາ..."
            className="h-9 w-full"
            clearable
          />
        )}
      />
      <p className="text-muted-foreground text-xs">
        ບໍ່ເລືອກ = ສິດເຂົ້າທຸກສາຂາ (ແຖວດຽວ branch ວ່າງ). ເລືອກຫຼາຍ = ສິດຕາມສາຂາທີ່ລະບຸ.
      </p>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "ແກ້ໄຂພະນັກງານ" : "ເພີ່ມພະນັກງານ"}
      description={
        isEdit
          ? "ປັບບັນຊີ, ບົດບາດ, ສາຂາ (ຫຼາຍສາຂາໄດ້) ແລະ ສະຖານະ — ບັນທຶກຈະຈັງແຖວ shop_staff ຕາມສາຂາທີ່ເລືອກ"
          : "ສ້າງບັນຊີຜູ້ໃຊ້ໃໝ່ແລະເພີ່ມເຂົ້າລາຍການພະນັກງານ (ຫຼາຍສາຂາໄດ້)"
      }
      size="md"
    >
      {loading || (isEdit && userDetailsLoading) ? (
        <div className="flex min-h-[120px] items-center justify-center py-6">
          <Loader />
        </div>
      ) : isEdit ? (
        <FormRoot<UpdateFormValues>
          methods={updateForm}
          onSubmit={(vals) => onSubmitUpdate(vals)}
          className="space-y-4"
        >
          <FormInput
            name="email"
            label="ອີເມວ"
            type="email"
            requiredMark
            placeholder="name@example.com"
          />
          <FormInput
            name="name"
            label="ຊື່"
            requiredMark
            placeholder="John Doe"
          />
          <FormPassword
            name="password"
            label="ລະຫັດຜ່ານ (ວ່າງໄວ້ຖ້າບໍ່ຕ້ອງການປ່ຽນ)"
            placeholder="********"
          />
          <FormSelect
            name="shopRoleId"
            label="ບົດບາດໃນຮ້ານ"
            options={[
              { label: "— ບໍ່ກຳນົດ —", value: SHOP_ROLE_NONE },
              ...shopRoleOptions,
            ]}
          />
          {branchCombo(updateForm.control, "branchIds")}
          <div className="flex items-center gap-2">
            <RHF.Controller
              name="isActive"
              control={updateForm.control}
              render={({ field }) => (
                <>
                  <Checkbox
                    id="modal-isActive"
                    checked={field.value}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  <Label
                    htmlFor="modal-isActive"
                    className="cursor-pointer text-sm"
                  >
                    ໃຊ້ງານຢູ່
                  </Label>
                </>
              )}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader /> : null}
              <span className="ml-2">ບັນທຶກ</span>
            </Button>
          </div>
        </FormRoot>
      ) : (
        <FormRoot<CreateFormValues>
          methods={createForm}
          onSubmit={(vals) => onSubmitCreate(vals)}
          className="space-y-4"
        >
          <FormInput
            name="email"
            label="ອີເມວ"
            type="email"
            requiredMark
            placeholder="name@example.com"
          />
          <FormInput
            name="name"
            label="ຊື່"
            requiredMark
            placeholder="John Doe"
          />
          <FormPassword
            name="password"
            label="ລະຫັດຜ່ານ"
            placeholder="********"
            requiredMark
          />
          <FormSelect
            name="shopRoleId"
            label="ບົດບາດໃນຮ້ານ"
            options={[
              { label: "— ບໍ່ກຳນົດ —", value: SHOP_ROLE_NONE },
              ...shopRoleOptions,
            ]}
          />
          {branchCombo(createForm.control, "branchIds")}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader /> : null}
              <span className="ml-2">ເພີ່ມພະນັກງານ</span>
            </Button>
          </div>
        </FormRoot>
      )}
    </Modal>
  );
}
